import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type AdsMappingStatus = 'UNASSIGNED' | 'PARTIAL' | 'NEEDS_CONFIRMATION' | 'CONFIRMED'

export type AdsMappingCityOption = {
  city_id: number
  city_name: string
}

export type AdsAccountMappingRow = {
  account_id: number
  channel: string
  partner_number: number | null
  partner_name: string | null
  account_name: string | null
  account_name_snapshot: string | null
  mapped_targetolog_id: string | null
  mapped_targetolog_name: string | null
  mapped_city_id: number | null
  mapped_city_name: string | null
  suggested_city_id: number | null
  suggested_city_name: string | null
  city_options: AdsMappingCityOption[]
  city_options_count: number
  is_confirmed: boolean
  source: 'manual' | 'auto'
  mapping_status: AdsMappingStatus
  spent_30d: number
  goals_30d: number
  last_stat_date: string | null
  name_changed: boolean
}



export type AdsAccountMappingSignalCode =
  | 'MATCH_LOST'
  | 'TARGET_AMBIGUOUS'
  | 'TARGET_CHANGED'
  | 'CITY_AMBIGUOUS'
  | 'CITY_CHANGED'
  | 'NAME_CHANGED'

export type AdsAccountMappingSignal = {
  account_id: number
  signal_code: AdsAccountMappingSignalCode
  signal_level: 'warning' | 'error'
  signal_message: string
  suggested_targetolog_id: string | null
  suggested_targetolog_name: string | null
  suggested_city_id: number | null
  suggested_city_name: string | null
  previous_account_name: string | null
  current_account_name: string | null
}

export type AdsAccountMappingSyncResult = {
  inserted_auto: number
  snapshots_refreshed: number
  conflicts_reopened: number
}

export type AdsTargetologRow = {
  id: string
  display_name: string
  is_active: boolean
  created_at: string
  updated_at: string
}

function asNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function asNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeCityOptions(value: unknown): AdsMappingCityOption[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const raw = item as Record<string, unknown>
      const cityId = asNullableNumber((item as Record<string, unknown>).city_id)
      const cityName = typeof raw.city_name === 'string'
        ? raw.city_name.trim()
        : ''
      if (cityId === null || !cityName) return null
      return { city_id: cityId, city_name: cityName }
    })
    .filter((item): item is AdsMappingCityOption => item !== null)
}

function normalizeMappingRow(row: Record<string, unknown>): AdsAccountMappingRow {
  return {
    account_id: asNumber(row.account_id),
    channel: typeof row.channel === 'string' ? row.channel : '',
    partner_number: asNullableNumber(row.partner_number),
    partner_name: typeof row.partner_name === 'string' ? row.partner_name : null,
    account_name: typeof row.account_name === 'string' ? row.account_name : null,
    account_name_snapshot: typeof row.account_name_snapshot === 'string' ? row.account_name_snapshot : null,
    mapped_targetolog_id: typeof row.mapped_targetolog_id === 'string' ? row.mapped_targetolog_id : null,
    mapped_targetolog_name: typeof row.mapped_targetolog_name === 'string' ? row.mapped_targetolog_name : null,
    mapped_city_id: asNullableNumber(row.mapped_city_id),
    mapped_city_name: typeof row.mapped_city_name === 'string' ? row.mapped_city_name : null,
    suggested_city_id: asNullableNumber(row.suggested_city_id),
    suggested_city_name: typeof row.suggested_city_name === 'string' ? row.suggested_city_name : null,
    city_options: normalizeCityOptions(row.city_options),
    city_options_count: asNumber(row.city_options_count),
    is_confirmed: Boolean(row.is_confirmed),
    source: row.source === 'auto' ? 'auto' : 'manual',
    mapping_status: (
      row.mapping_status === 'PARTIAL'
        ? 'PARTIAL'
        : row.mapping_status === 'NEEDS_CONFIRMATION'
          ? 'NEEDS_CONFIRMATION'
          : row.mapping_status === 'CONFIRMED'
            ? 'CONFIRMED'
            : 'UNASSIGNED'
    ),
    spent_30d: asNumber(row.spent_30d),
    goals_30d: asNumber(row.goals_30d),
    last_stat_date: typeof row.last_stat_date === 'string' ? row.last_stat_date : null,
    name_changed: Boolean(row.name_changed),
  }
}



function normalizeSignalRow(row: Record<string, unknown>): AdsAccountMappingSignal {
  const rawCode = typeof row.signal_code === 'string' ? row.signal_code : 'NAME_CHANGED'
  const signalCode: AdsAccountMappingSignalCode = (
    rawCode === 'MATCH_LOST'
      ? 'MATCH_LOST'
      : rawCode === 'TARGET_AMBIGUOUS'
        ? 'TARGET_AMBIGUOUS'
        : rawCode === 'TARGET_CHANGED'
          ? 'TARGET_CHANGED'
          : rawCode === 'CITY_AMBIGUOUS'
            ? 'CITY_AMBIGUOUS'
            : rawCode === 'CITY_CHANGED'
              ? 'CITY_CHANGED'
              : 'NAME_CHANGED'
  )

  return {
    account_id: asNumber(row.account_id),
    signal_code: signalCode,
    signal_level: row.signal_level === 'error' ? 'error' : 'warning',
    signal_message: typeof row.signal_message === 'string' ? row.signal_message : 'Проверьте сопоставление кабинета',
    suggested_targetolog_id: typeof row.suggested_targetolog_id === 'string' ? row.suggested_targetolog_id : null,
    suggested_targetolog_name: typeof row.suggested_targetolog_name === 'string' ? row.suggested_targetolog_name : null,
    suggested_city_id: asNullableNumber(row.suggested_city_id),
    suggested_city_name: typeof row.suggested_city_name === 'string' ? row.suggested_city_name : null,
    previous_account_name: typeof row.previous_account_name === 'string' ? row.previous_account_name : null,
    current_account_name: typeof row.current_account_name === 'string' ? row.current_account_name : null,
  }
}

function normalizeSyncResult(row: Record<string, unknown> | null | undefined): AdsAccountMappingSyncResult {
  return {
    inserted_auto: asNumber(row?.inserted_auto),
    snapshots_refreshed: asNumber(row?.snapshots_refreshed),
    conflicts_reopened: asNumber(row?.conflicts_reopened),
  }
}

function normalizeTargetologRow(row: Record<string, unknown>): AdsTargetologRow {
  return {
    id: typeof row.id === 'string' ? row.id : '',
    display_name: typeof row.display_name === 'string' ? row.display_name : '',
    is_active: Boolean(row.is_active),
    created_at: typeof row.created_at === 'string' ? row.created_at : '',
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : '',
  }
}

export async function fetchAdsAccountMappings(params?: {
  search?: string
  status?: 'ALL' | 'ASSIGNED' | AdsMappingStatus
  channel?: string
  includeWithoutSpend?: boolean
}): Promise<AdsAccountMappingRow[]> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_ads_account_mappings', {
      p_search: params?.search?.trim() || null,
      p_status: params?.status ?? 'ALL',
      p_channel: params?.channel?.trim() || null,
      p_include_without_spend: params?.includeWithoutSpend ?? true,
    })
  ) as { data: Array<Record<string, unknown>> | null; error: any }

  if (error) throw error
  return (data ?? []).map(normalizeMappingRow)
}

export async function fetchAdsTargetologs(includeInactive = false): Promise<AdsTargetologRow[]> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_ads_targetologs', {
      p_include_inactive: includeInactive,
    })
  ) as { data: Array<Record<string, unknown>> | null; error: any }

  if (error) throw error
  return (data ?? []).map(normalizeTargetologRow)
}

export async function saveAdsAccountMapping(input: {
  accountId: number
  targetologId: string | null
  cityId: number | null
  isConfirmed?: boolean
  source?: 'manual' | 'auto'
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('save_ads_account_mapping_reviewed', {
      p_account_id: input.accountId,
      p_targetolog_id: input.targetologId,
      p_city_id: input.cityId,
      p_is_confirmed: input.isConfirmed ?? true,
      p_source: input.source ?? 'manual',
    })
  ) as { error: any }

  if (error) throw error
}



export async function syncAdsAccountMappings(): Promise<AdsAccountMappingSyncResult> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('sync_ads_account_mappings')
  ) as { data: Array<Record<string, unknown>> | Record<string, unknown> | null; error: any }

  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return normalizeSyncResult(row)
}

export async function fetchAdsAccountMappingSignals(): Promise<AdsAccountMappingSignal[]> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_ads_account_mapping_signals')
  ) as { data: Array<Record<string, unknown>> | null; error: any }

  if (error) throw error
  return (data ?? []).map(normalizeSignalRow)
}

export async function createAdsTargetolog(displayName: string): Promise<AdsTargetologRow> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('create_ads_targetolog', {
      p_display_name: displayName,
    })
  ) as { data: Record<string, unknown> | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Не удалось создать таргетолога')
  return normalizeTargetologRow(data)
}

export async function renameAdsTargetolog(targetologId: string, displayName: string): Promise<AdsTargetologRow> {
  const trimmed = displayName.trim()
  if (!trimmed) throw new Error('Имя таргетолога не может быть пустым')

  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('rename_ads_targetolog', {
      p_targetolog_id: targetologId,
      p_display_name: trimmed,
    })
  ) as { data: Record<string, unknown> | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Не удалось переименовать таргетолога')
  return normalizeTargetologRow(data)
}

export async function setAdsTargetologActive(targetologId: string, isActive: boolean): Promise<AdsTargetologRow> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_ads_targetolog_active_preserve_mappings', {
      p_targetolog_id: targetologId,
      p_is_active: isActive,
    })
  ) as { data: Record<string, unknown> | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Не удалось обновить статус таргетолога')
  return normalizeTargetologRow(data)
}
